'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { usePusdtNudgeModal } from '@flipster/features/src/flipster-earn/premium-usdt/pusdt-nudge-modal';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { earnSummaryBunja, getEarnSummaryOfCurrency } from '@flipster/features/src/shared/earn/state';
import { useDepositModal } from '@flipster/features/src/shared/wallet-modal/deposit';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { useOverlay } from '@flipster/overlay';
import { Button } from '@flipster/react/design-system/button';
import { css } from '@flipster/styled-system/css';
import { stack, vstack } from '@flipster/styled-system/patterns';
import { PusdtCurrency } from '@flipster/utils/constants/currency';
import segment from '@flipster/utils/segment';
import { EarnEvent } from '@flipster/utils/segment/events/earn';

import { CurrencyHeader } from './currency-header';
import { EarnActionModal } from './earn-action-modal';

export const InactiveAprCard = ({ currency, addDisabled = false }: { currency: string; addDisabled?: boolean }) => {
  const t = useTranslations();
  const router = useRouter();
  const overlay = useOverlay();
  const openDepositModal = useDepositModal();

  const openPusdtNudgeModal = usePusdtNudgeModal();
  const { isLoggedIn } = useAuth();
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);

  const earnSummary = useAtomValue(earnSummaryAtom);

  const { baseAprPct, maxAprPct, isMintAvailable, isTradeAvailable } = getEarnSummaryOfCurrency(currency, earnSummary);

  const isMaxEqBase = maxAprPct.eq(baseAprPct);
  // Open action modal when mint and trade is available
  const needToOpenActions = isMintAvailable || isTradeAvailable;
  const isPusdt = currency === PusdtCurrency;

  const onClickGetHigherApr = () => {
    segment.track(EarnEvent.EarnManageClick, { button_text: 'Get higher APR', currency });
    openPusdtNudgeModal();
  };

  const onClickDeposit = () => {
    segment.track(EarnEvent.EarnManageClick, { button_text: 'Deposit', currency });
    if (!isLoggedIn) {
      router.push('/login');
      return;
    }
    openDepositModal({ defaultValues: { currency } });
  };

  const onClickSubscribe = () => {
    segment.track(EarnEvent.EarnManageClick, { button_text: 'Subscribe', currency });
    overlay.open(({ isOpen, onClose, unmount }) => (
      <EarnActionModal isOpen={isOpen} onClose={onClose} currency={currency} onExit={unmount} />
    ));
  };

  return (
    <div className={stack({ gap: 8, bg: 'background.default.level-1', p: 20, rounded: 8 })}>
      <CurrencyHeader currency={currency} />
      <div className={vstack({ gap: 2, alignSelf: 'center', pb: 8 })}>
        <p className={css({ textStyle: 'Title/M', color: 'content.accent.info' })}>
          <FormattedMessage
            id='Autogen_common.r251126.s_maxAprPct_toString_APR'
            values={{
              maxAprPct_toString: maxAprPct.toString(),
            }}
          />
        </p>
        {isMaxEqBase ? null : (
          <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
            <FormattedMessage
              id='Earn.r260120.Symbol_card_desription'
              values={{
                base: `${baseAprPct.toString()}%`,
                maximum: `${maxAprPct.toString()}%`,
              }}
            />
          </p>
        )}
      </div>
      {needToOpenActions && (
        <Button
          label={t('Autogen_common.r251126.s_Add')}
          onClick={addDisabled ? undefined : onClickSubscribe}
          disabled={addDisabled}
          css={{ w: { base: '100%', tablet: 296 }, alignSelf: 'center' }}
        />
      )}
      {!needToOpenActions && (
        <Button
          label={
            isPusdt ? (
              <FormattedMessage id='Autogen_common.r251126.s_Add' />
            ) : (
              <FormattedMessage id='Common.r251107.Deposit' />
            )
          }
          onClick={addDisabled ? undefined : isPusdt ? onClickGetHigherApr : onClickDeposit}
          disabled={addDisabled}
          css={{ w: { base: '100%', tablet: 296 }, alignSelf: 'center' }}
        />
      )}
    </div>
  );
};
